import React, { Component } from "react";
import PropTypes from "prop-types";
import GoogleMap from "google-map-react";
import { GOOGLE_MAP_SETTINGS, GOOGLE_MAP_OPTIONS } from "@constants";
import utils from "@utils";
import { Modal } from "../../../../../components";
import MapMarker from "./MapMarker";

import "./JobsMapModal.scss";

class JobsMapModal extends Component {
	constructor(props) {
		super(props);

		this.state = {
			mapCenter: GOOGLE_MAP_SETTINGS.defaultCenter,
			mapZoom: 10,
			selectedJob: undefined,
		};
	}

	componentDidUpdate({ show: prevShow }) {
		const { show, jobs } = this.props;
		if (prevShow !== show && show && jobs.length > 0) {
			this.centerForAllJobs(jobs);
		}
	}

	/**
	 * Determines a reasonable map center (coordinates) based on the coordinates of locations being viewed
	 * @param {Array} jobs A list of jobs to be shown on the map
	 */
	centerForAllJobs = jobs => {
		const coordinateList = jobs.map(({ LocationLatitude: lat, LocationLongitude: lng }) => ({
			lat,
			lng,
		}));
		const lats = coordinateList.map(({ lat }) => lat).sort((a, b) => a - b);
		const lngs = coordinateList.map(({ lng }) => lng).sort((a, b) => a - b);
		let lat = 0;
		let lng = 0;
		if (coordinateList.length === 2) {
			lat = lats[0] + lats[1] / 2;
			lng = lngs[0] + lngs[1] / 2;
		} else if (coordinateList.length % 2 === 0) {
			// If even number of results
			lat = (lats[coordinateList.length / 2] + lats[coordinateList.length / 2 + 1]) / 2;
			lng = (lngs[coordinateList.length / 2] + lngs[coordinateList.length / 2 + 1]) / 2;
		} else if (coordinateList.length > 1) {
			lat = lats[Math.ceil(coordinateList.length / 2)];
			lng = lngs[Math.ceil(coordinateList.length / 2)];
		} else {
			[lat] = lats;
			[lng] = lngs;
		}
		this.setState({ mapCenter: { lat, lng }, mapZoom: 10, selectedJob: undefined });
	};

	/**
	 * @param {Object} job The data object representing a job
	 * @returns {String} formatted start and end times for the job
	 */
	getJobTimes = job => {
		const { timezone, user } = this.props;
		const { StartDateTime, EndDateTime } = job;
		return `${utils.formatTime(new Date(`${StartDateTime}Z`), user.UserPreferences !== "useMilitaryTime", timezone)} - 
			${utils.formatTime(new Date(`${EndDateTime}Z`), user.UserPreferences !== "useMilitaryTime", timezone)}`;
	};

	/**
	 * @param {Object} job The job to be highlighted on the map
	 */
	onJobChange = job => {
		const { Id, LocationLatitude: lat, LocationLongitude: lng } = job;
		this.setState({ selectedJob: Id, mapCenter: { lat, lng }, mapZoom: 15 });
	};

	onExited = () => {
		const { onCancel } = this.props;
		onCancel();
	};

	render() {
		const { mapCenter, mapZoom, selectedJob } = this.state;
		const { show, activeGrid, jobs } = this.props;

		return (
			<Modal
				show={show}
				size="lg"
				id="jobs-map-modal"
				onHide={this.onExited}
			>
				<Modal.Header closeButton>
					<Modal.Title>
						<If condition={activeGrid}>
							<span className="text-capitalize">{activeGrid} </span>
						</If>
						Jobs
					</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<div className="row">
						<div className="col-sm-7">
							<div className="map-view">
								<If condition={jobs.length > 0}>
									<GoogleMap
										zoom={mapZoom}
										center={mapCenter}
										bootstrapURLKeys={{ key: GOOGLE_MAP_SETTINGS.key }}
										options={GOOGLE_MAP_OPTIONS}
										onZoomAnimationEnd={zoom =>
											this.setState({ mapZoom: zoom })
										}
									>
										<For
											each="job"
											of={jobs}
										>
											<MapMarker
												key={`job-marker_${job.Id}`}
												lat={job.LocationLatitude || 0}
												lng={job.LocationLongitude || 0}
												title={job.Name}
												message={this.getJobTimes(job)}
												highlightMarker={selectedJob === job.Id}
											/>
										</For>
									</GoogleMap>
								</If>
							</div>
						</div>
						<div className="col-sm-5 job-list">
							<For
								each="job"
								of={jobs}
							>
								<div
									key={`job_item_${job.Id}`}
									role="button"
									tabIndex="0"
									onClick={() => this.onJobChange(job)}
									onKeyPress={({ key }) => {
										if (key === "Enter") {
											this.onJobChange(job);
										}
									}}
									className={`row${job.Id === selectedJob ? " selected-job" : ""}`}
								>
									<div className="col-xs-6 col-sm-12 col-md-6">
										<strong>{job.Name}</strong>
										<br />
										<small>{job.ClientName}</small>
										<br />
										<small>{this.getJobTimes(job)}</small>
									</div>
									<br className="visible-sm" />
									<div className="col-xs-6 col-sm-12 col-md-6 text-right">
										<strong>{job.LocationName}</strong>
										<br className={job.LocationName ? "" : "hidden"} />
										{job.LocationAddress}
										<br className={job.LocationAddress ? "" : "hidden"} />
										{job.LocationCity}
										{(job.LocationCity && job.LocationState) ||
										(job.LocationCity && job.LocationZipcode)
											? ", "
											: ""}
										{job.LocationState}
										&nbsp;
										{job.LocationZipcode}
									</div>
								</div>
							</For>
						</div>
					</div>
				</Modal.Body>
				<Modal.Footer>
					<button
						type="button"
						className="btn btn-link"
						onClick={this.onExited}
					>
						Close
					</button>
				</Modal.Footer>
			</Modal>
		);
	}
}

JobsMapModal.propTypes = {
	timezone: PropTypes.string,
	show: PropTypes.bool,
	onCancel: PropTypes.func,
	activeGrid: PropTypes.string,
	jobs: PropTypes.array,
	user: PropTypes.any.isRequired,
};

JobsMapModal.defaultProps = {
	timezone: undefined,
	show: false,
	onCancel: () => {},
	activeGrid: undefined,
	jobs: [],
};

export default JobsMapModal;
